import Decimal from 'decimal.js';

import * as hallApi from '@/api/hall/index';
import { ACCESS_PIXEL_KEY, ACCESS_USER_NAME, ACCESS_USERID_KEY, USER_ACTION_QUIT_KEY } from '@/constant/config';
import { modalStore } from '@/stores/modal';

import { isInApk, openInBrowser } from './apk';
import Storage from './Storage';

export const uuid = (length = 16) => {
  let uuid = '';
  const hexDigits = '0123456789abcdefghijklmnopqrstuvwxyz';
  for (let i = 0; i < length; i++) {
    uuid += hexDigits[Math.floor(Math.random() * hexDigits.length)];
  }
  return uuid;
};

/**
 * 获取唯一标识
 */
export const getUniqueKey = async (): Promise<string> => {
  const uniqueKey = localStorage.getItem('us_game_unique_key');
  if (uniqueKey) {
    return uniqueKey;
  }

  const fingerprint = await createFingerprint();
  localStorage.setItem('us_game_unique_key', fingerprint as string);

  return fingerprint as string;
};

export const createFingerprint = () => {
  // 选择哪些信息作为浏览器指纹生成的依据
  const options = {
    fonts: {
      extendedJsFonts: true
    },
    excludes: {
      enumerateDevices: true,
      touchSupport: true
    }
  };
  return new Promise(resolve => {
    // 浏览器指纹
    Fingerprint2.get(options, components => {
      // 参数只有回调函数或者options为{}时，默认浏览器指纹依据所有配置信息进行生成
      const values = components.map(component => component.value); // 配置的值的数组
      const murmur = Fingerprint2.x64hash128(values.join(''), 31); // 生成浏览器指纹
      resolve(murmur);
    });
  });
};
/**
 * 获取url参数
 */
export const getUrlParam = (name: string) => {
  const reg = new URLSearchParams(window.location.search);
  return reg.get(name);
};

/**
 * 判断设备类型是ios还是android还是pc
 * @returns {string} ios | android | pc
 */
export const getDevice = () => {
  if (/(iPhone|iPad|iPod|iOS)/i.test(navigator.userAgent)) {
    return 'ios';
  } else if (/(Android)/i.test(navigator.userAgent)) {
    return 'android';
  } else {
    return 'pc';
  }
};

export function stringify(data: Object) {
  const arr: string[] = [];
  for (const key in data) {
    if (data[key]) {
      arr.push(`${key}=${encodeURIComponent(data[key])}`);
    }
  }
  return arr.join('&');
}

export const download = () => {
  const device = getDevice();
  if (isTictokApp()) {
    return;
  }
  if (device === 'ios') {
    modalStore.openDownload();
    return;
  }

  copyQilinid?.();
  const source = getAPKCHSource();
  window.location.href = `https://dmkr6yboyii3c.cloudfront.net/remote/brazil/download/${source}.apk`;
};

/**
 * 是否 kwai app中
 */
export function isKwaiApp() {
  const channel = Storage.get('channel_type');
  return /Kwai/.test(navigator.userAgent) || channel === 'kwai';
}

export function isTictokApp() {
  const ua = navigator.userAgent;
  const channel = Storage.get('channel_type');
  return ua.includes('TikTok') || ua.includes('Bytedance') || channel === 'tiktok';
}

export function remToPx(remValue) {
  const htmlEle = document.querySelector('html');
  const currentFontSize = parseFloat(
    window.getComputedStyle(htmlEle as HTMLHtmlElement, null).getPropertyValue('font-size')
  );
  return remValue * currentFontSize;
}

export function isHideDownload() {
  const { downloagFlag } = getChannelFlagConfig();
  return downloagFlag !== '1';
}

export function isPwaSource() {
  const { downloagFlag } = getChannelFlagConfig();
  return !downloagFlag || downloagFlag === '0';
}

export function getPixels() {
  return Storage.get(ACCESS_PIXEL_KEY);
}

export function getChannelFlag() {
  const source = getAPKCHSource().toLocaleUpperCase();

  return window.localStorage.getItem(`${source}_us_web_switch_flag`) as string;
}

export function getChannelFlagConfig() {
  const flags = getChannelFlag();
  const [downloagFlag, registerFlag] = flags?.split?.('') || [];
  return { downloagFlag, registerFlag };
}

export function getDownloadType() {
  const { downloagFlag } = getChannelFlagConfig();
  const isPwaInstall = downloagFlag === '0';
  const isApkDownload = downloagFlag === '1';
  const isHide = downloagFlag === '2';
  return {
    isPwaInstall,
    isApkDownload,
    isHide
  };
}

export function encryptPhone(str = '') {
  return str?.replace(/(\d{3})\d{3}(\d{3})/, '$1***$2');
}

export function inPwaEnv() {
  return (
    ('standalone' in window.navigator && window.navigator.standalone) ||
    window.matchMedia('(display-mode: standalone)').matches
  );
}

export function isInFacebookBrowser() {
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
  return ua.indexOf('FBAV') !== -1 || ua.indexOf('FBAN') !== -1 || ua.indexOf('FB_IAB') !== -1;
}

export function isInChromeBrowser() {
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
  return ua.indexOf('Chrome') !== -1 || ua.indexOf('CriOS') !== -1;
}

export function isGuestUser() {
  const username = Storage.getAccess(ACCESS_USER_NAME);
  return username?.startsWith('guest');
}

export function removeDiacritics(str) {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

// 只保留字符串中的26个字母
export function removeNumberByString(str) {
  return str.replace(/\s/g, '').replace(/[^a-zA-Z]/g, '');
}

/**
 *  判断是否主动退出
 *  */
export function isUserQuit() {
  return Storage.get(USER_ACTION_QUIT_KEY) === '1';
}

/**
 * 保留小数
 */
export const toFixed = (num, decimals = 2, type: any = Decimal.ROUND_DOWN) => {
  return new Decimal(num).toFixed(decimals, type);
};

/** 去掉末尾无效的0 */
export const removeTrailingZeros = (num: string) => {
  return num.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
};

/** 睡眠 */
export const sleep = (time = 300) => {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve(true);
    }, time);
  });
};
/** 判断是否是在 safari 浏览器 */
export function isSafari() {
  const userAgent = navigator.userAgent;
  const vendor = navigator.vendor;

  // 检查是否是 Safari
  const isSafari = /Safari/.test(userAgent) && /Apple Computer/.test(vendor);

  // 排除 Chrome，因为 Chrome 的用户代理字符串也包含 "Safari"
  const isChrome = /Chrome/.test(userAgent);

  return isSafari && !isChrome;
}
/** 获取浏览器的名称 */
export const getBrowser = () => {
  const userAgent = navigator.userAgent;

  // 判断 iOS 上的 Chrome
  if (/CriOS/.test(userAgent)) {
    return 'chrome';
  }

  // 判断 iOS 上的 Safari
  if (/iPhone/.test(userAgent) && /Safari/.test(userAgent)) {
    return 'safari';
  }

  // 判断 Android 上的 Chrome
  if (/CriOS/.test(userAgent) || /Chrome/.test(userAgent)) {
    return 'chrome';
  }

  // 其他浏览器
  return 'other';
};

export function suitStr(str: string, length = 10) {
  if (str.length <= length) {
    return str;
  }
  return str.slice(0, length) + '...';
}
/** 取值范围 */
export const clamp = (number, min, max) => Math.max(min, Math.min(number, max));

/** 根据渠道获取产品名 */
const getKefuProduct = async () => {
  const productMap = {
    default: 'usa',

    // 联运 3
    h5us4hz09: 'usa_ly3',
    h5us4apkhz02: 'usa_ly3',

    // 捕鱼
    h5us4fish: 'usa_fish',

    // 分包 3-自营 2
    h5us4fs: 'usa_funbet',

    // 分包4-联运一-Us测试-三哥
    h5us4hz08: 'usa_slotworld',

    // 分包6-联运五-联运5-问玉(8+1)-三哥
    h5us4hz11: 'usa_luckyslot'
  };

  const source = getAPKCHSource().replace('CH_', '').toLocaleLowerCase().split('_')?.[0];

  if (['h5us4hz10', 'h5us4hz13'].includes(source)) {
    // 分包5-联运四-内部交流群-三哥
    return { botName: 'playslotcom', isBot: false, product: 'usa_playslot' };
  }

  const product = productMap[source] || productMap['default'];

  const { bot_username: botName } = await hallApi.getKefuName(product);

  const isBot = (botName || '').toLowerCase().endsWith('bot');
  return { product, botName, isBot };
};

/** 获取客服链接 */
export const getKeFuUrl = async () => {
  // country, platform, ver, source, userid 按这个顺序 base64

  const { botName, isBot, product } = await getKefuProduct();
  if (!botName) {
    // 没有获取到数据的情况，此情况属于异常情况，后续可能需要在这里添加提示
    return '';
  }

  if (!isBot) {
    return `https://t.me/${botName}`;
  }

  // 机器人
  const source = getAPKCHSource().replace('CH_', '').toLocaleLowerCase();
  const userid = Storage.getAccess(ACCESS_USERID_KEY);
  const platform = inPwaEnv() ? 'h5pwa' : isInApk() ? 'h5apk' : 'h5';
  const area = product || window?.privateConfig?.botArea || 'usa';
  const query = window.btoa(`${area},${platform},1.0.0,${source},${userid}`);
  return `https://t.me/${botName}?start=${query}`;
};

export const openKefu = async () => {
  const device = getDevice();
  const isIos = device === 'ios';
  if (isInApk()) {
    const url = await getKeFuUrl();
    openInBrowser(url);
  } else if (isIos) {
    const newwin = window.open('', '_blank');
    const url = await getKeFuUrl();
    (newwin as Window).location.href = url;
  } else {
    const url = await getKeFuUrl();
    window.open(url);
  }
};

export function getRandomMarqueeList() {
  const ret: any[] = [];
  for (let i = 0; i < 10; i++) {
    ret.push({
      userId: '50****' + Math.floor(10 + Math.random() * 89),
      cash: Math.random() < 0.5 ? '$30' : '5 USDT'
    });
  }
  return ret;
}
